/*
  Phone model selector. Markup, labels and settings: snippets/phone-selector.liquid (header),
  blocks/phone-model-bar.liquid (collection pages); the model list comes from sections/phone-model-catalog.liquid.

  Two remembered choices:
  - "My phone"     (localStorage) — set from the header; the store-wide default.
  - "Shopping for" (sessionStorage, this tab only) — set from the collection bar or the product page,
                    e.g. a gift for someone else. "*" means "show all models" for this visit.
  Which model a page uses: a model in the link itself > "shopping for" > "my phone" > none.

  - Collection pages: Shopify's Phone Model filter (Search & Discovery, filter.v.option.phone model) does the
    filtering; a page opened without it is redirected to the filtered URL. Product cards on a filtered
    collection link to the matching variant.
  - Product pages: the phone model (always the product's first option, matched by position because option
    names can be translated) is chosen in a field that opens the panel; Fabric's picker buttons for it are
    hidden. If the chosen model is not sold in the selected case type, a case type that exists is picked.
  - Every collection link that is clicked carries the model.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-phone-selector]');
  if (!root) return;
  // The header creates its own containing block (sticky/transform), so the panel lives on <body> to cover the viewport
  document.body.appendChild(root);

  var MINE_KEY = 'phoneModel';
  var SHOPPING_KEY = 'phoneModelShopping';
  var ALL = '*';
  var BRAND_ORDER = ['Apple', 'Samsung', 'Google', 'Xiaomi', 'OnePlus'];
  var data = root.dataset;
  var param = data.filterParam || 'filter.v.option.phone model';
  var pageType = data.pageType;
  var list = root.querySelector('[data-phone-selector-list]');
  var search = root.querySelector('[data-phone-selector-search]');
  var title = root.querySelector('[data-phone-selector-title]');
  var back = root.querySelector('[data-phone-selector-back]');
  var loadingText = (list.querySelector('[data-phone-selector-status]') || {}).textContent || '';

  var catalog = [];
  var catalogState = 'idle';
  var context = 'mine';
  var openBrand = null;
  var lastTrigger = null;

  /* ---------- remembered choices ---------- */

  function read(storage, key) {
    try {
      return window[storage].getItem(key) || '';
    } catch (error) {
      return '';
    }
  }

  function write(storage, key, value) {
    try {
      if (value) window[storage].setItem(key, value);
      else window[storage].removeItem(key);
    } catch (error) {
      /* storage unavailable (private mode): the choice only lasts for this page */
    }
  }

  var mine = read('localStorage', MINE_KEY);
  var shopping = read('sessionStorage', SHOPPING_KEY);

  function setMine(value) {
    mine = value;
    write('localStorage', MINE_KEY, value);
  }

  function setShopping(value) {
    shopping = value === mine && value !== ALL ? '' : value;
    write('sessionStorage', SHOPPING_KEY, shopping);
  }

  function effective() {
    if (shopping === ALL) return '';
    return shopping || mine;
  }

  function label(name, model) {
    return (data[name] || '').replace('__MODEL__', model || '');
  }

  /* ---------- collection page: the URL filter is the truth ---------- */

  function withModel(href, model) {
    var url = new URL(href, window.location.origin);
    url.searchParams.delete(param);
    url.searchParams.delete('page');
    if (model) url.searchParams.set(param, model);
    return url;
  }

  if (pageType === 'collection' && data.filterAvailable === 'true') {
    var urlModel = data.activeModel || '';
    if (urlModel) {
      if (urlModel !== effective()) setShopping(urlModel);
    } else if (effective() && !new URLSearchParams(window.location.search).has(param)) {
      // Opened without a model (e.g. a typed URL): show this collection for the remembered phone.
      // Not when the URL already carries the filter: a model with no matching filter value (no active
      // value reported) would otherwise redirect to the same URL forever, which flickers the page.
      window.location.replace(withModel(window.location.href, effective()).toString());
      return;
    }
  }

  /* ---------- header trigger + collection bar ---------- */

  function updateTriggers() {
    document.querySelectorAll('[data-phone-selector-context="mine"]').forEach(function (trigger) {
      var value = trigger.querySelector('[data-phone-selector-current]');
      if (value) value.textContent = mine;
      trigger.setAttribute('aria-label', data.labelChoose + (mine ? ': ' + mine : ''));
    });
  }

  function barButton(text, onClick, primary) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'phone-model-bar__action' + (primary ? ' phone-model-bar__action--primary' : '');
    button.textContent = text;
    button.addEventListener('click', onClick);
    return button;
  }

  function separator() {
    var span = document.createElement('span');
    span.className = 'phone-model-bar__separator';
    span.setAttribute('aria-hidden', 'true');
    span.textContent = '·';
    return span;
  }

  function renderBar() {
    var bar = document.querySelector('[data-phone-model-bar]');
    if (!bar) return;
    bar.replaceChildren();
    bar.hidden = false;
    var model = effective();
    var text = document.createElement('span');
    text.className = 'phone-model-bar__text';
    var parts = [];

    function openFromBar(event) {
      open(event.currentTarget, 'shopping');
    }

    if (model) {
      text.textContent = shopping && shopping !== ALL ? label('labelShoppingFor', model) : label('labelCasesFor', model);
      parts.push(barButton(data.labelChange, openFromBar));
      if (shopping && shopping !== ALL && mine) {
        parts.push(
          barButton(label('labelBackToMine', mine), function () {
            setShopping('');
            applyToPage();
          })
        );
      }
      parts.push(
        barButton(data.labelShowAll, function () {
          setShopping(ALL);
          applyToPage();
        })
      );
    } else if (shopping === ALL) {
      text.textContent = data.labelShowingAll;
      parts.push(barButton(data.labelChooseModel, openFromBar, true));
      if (mine) {
        parts.push(
          barButton(label('labelBackToMine', mine), function () {
            setShopping('');
            applyToPage();
          })
        );
      }
    } else {
      text.textContent = data.labelQuestion;
      parts.push(barButton(data.labelChooseModel, openFromBar, true));
    }

    bar.appendChild(text);
    parts.forEach(function (part, index) {
      if (index > 0 || !part.classList.contains('phone-model-bar__action--primary')) bar.appendChild(separator());
      bar.appendChild(part);
    });
  }

  /* ---------- product page: model field ---------- */

  function picker() {
    return document.querySelector('variant-picker');
  }

  function productModels() {
    var element = picker();
    if (!element) return [];
    var radios = element.querySelectorAll('input[type="radio"][data-input-id^="1-"]');
    if (radios.length) {
      return Array.prototype.map.call(radios, function (input) {
        return input.value;
      });
    }
    return Array.prototype.map.call(element.querySelectorAll('select option[data-input-id^="1-"]'), function (option) {
      return option.value;
    });
  }

  function productSelectedModel() {
    var element = picker();
    if (!element) return '';
    var checked = element.querySelector('input[type="radio"][data-input-id^="1-"]:checked');
    if (checked) return checked.value;
    var option = element.querySelector('select option[data-input-id^="1-"]:checked');
    return option ? option.value : '';
  }

  function selectGalleryMediaForModel(model) {
    var gallery = document.querySelector('media-gallery');
    if (!gallery || !model) return;
    var modelTag = '#phone-model_' + model.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    var slideshow = gallery.querySelector('slideshow-component');
    if (!slideshow || typeof slideshow.select !== 'function') return;
    var slides = Array.prototype.slice.call(slideshow.querySelectorAll('slideshow-slide'));
    var index = slides.findIndex(function (slide) {
      return Array.prototype.some.call(slide.querySelectorAll('img[alt]'), function (image) {
        return (image.getAttribute('alt') || '').toLowerCase().indexOf(modelTag) !== -1;
      });
    });
    if (index === -1) {
      index = slides.findIndex(function (slide) {
        return Array.prototype.some.call(slide.querySelectorAll('img[alt]'), function (image) {
          return (image.getAttribute('alt') || '').toLowerCase().indexOf('#phone-model_iphone-18-pro-max') !== -1;
        });
      });
    }
    if (index !== -1) slideshow.select(index, undefined, { animate: false });
  }

  var field = null;
  var fieldNote = '';

  function updateProductDescriptionModel(model) {
    var value = (model || '').trim() || 'your selected phone model';
    document.querySelectorAll('[data-phone-model-placeholder]').forEach(function (placeholder) {
      placeholder.textContent = value;
    });
  }

  function renderField() {
    var element = picker();
    if (!element) return;
    if (!field || !field.isConnected) {
      field = document.createElement('div');
      field.className = 'phone-model-field';
      field.innerHTML =
        '<span class="phone-model-field__label"></span>' +
        '<button type="button" class="phone-model-field__button" data-phone-selector-trigger data-phone-selector-context="product" aria-haspopup="dialog" aria-controls="PhoneSelectorPanel" aria-expanded="false">' +
        '<span class="phone-model-field__value"></span>' +
        '<svg aria-hidden="true" focusable="false" viewBox="0 0 10 6" width="10" height="6"><path fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" d="M1 1l4 4 4-4"/></svg>' +
        '</button>' +
        '<p class="phone-model-field__note" hidden></p>';
      element.parentNode.insertBefore(field, element);
      document.documentElement.classList.add('phone-model-field-ready');
    }
    var selected = productSelectedModel();
    updateProductDescriptionModel(selected);
    field.querySelector('.phone-model-field__label').textContent = data.labelField;
    field.querySelector('.phone-model-field__value').textContent = selected || data.labelFieldChoose;
    var note = field.querySelector('.phone-model-field__note');
    note.textContent = fieldNote;
    note.hidden = !fieldNote;
    renderCasePrices();
  }

  /* ---------- variant picker helpers ---------- */

  var productVariants = null;
  function loadVariants() {
    if (productVariants) return productVariants;
    var element = picker();
    var handle = element && element.dataset.productHandle;
    if (!handle) return Promise.resolve([]);
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    productVariants = fetch(base + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.json() : { variants: [] };
      })
      .then(function (product) {
        return product.variants || [];
      })
      .catch(function () {
        return [];
      });
    return productVariants;
  }

  function chooseOption(position, value) {
    var element = picker();
    if (!element || !value) return false;
    var prefix = position + '-';
    var radio = Array.prototype.filter.call(
      element.querySelectorAll('input[type="radio"][data-input-id^="' + prefix + '"]'),
      function (input) {
        return input.value === value;
      }
    )[0];
    if (radio) {
      if (!radio.checked) radio.click();
      return true;
    }
    var select = Array.prototype.filter.call(element.querySelectorAll('select'), function (item) {
      return item.querySelector('option[data-input-id^="' + prefix + '"]');
    })[0];
    if (!select) return false;
    var exists = Array.prototype.some.call(select.options, function (item) {
      return item.value === value;
    });
    if (!exists) return false;
    if (select.value !== value) {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return true;
  }

  function selectedCaseType() {
    var element = picker();
    if (!element) return '';
    var checked = element.querySelector('input[type="radio"][data-input-id^="2-"]:checked');
    if (checked) return checked.value;
    var option = element.querySelector('select option[data-input-id^="2-"]:checked');
    return option ? option.value : '';
  }

  // Not every phone model is sold in every case type (e.g. Galaxy A57: Tough only)
  function ensureCaseType(model) {
    loadVariants().then(function (variants) {
      var forModel = variants.filter(function (variant) {
        return variant.option1 === model;
      });
      if (!forModel.length) return;
      var caseType = selectedCaseType();
      if (
        forModel.some(function (variant) {
          return variant.option2 === caseType;
        })
      )
        return;
      var target =
        forModel.filter(function (variant) {
          return variant.available;
        })[0] || forModel[0];
      if (!target || !target.option2) return;
      // The picker only renders case types that exist for the chosen model, so wait until it has
      // re-rendered for this model before picking one (up to ~4 seconds)
      var attempts = 0;
      (function tryChoose() {
        attempts += 1;
        if (productSelectedModel() === model && chooseOption(2, target.option2)) return;
        if (attempts < 27) window.setTimeout(tryChoose, 150);
      })();
    });
  }

  // Case type cards (snippets/case-type-cards.liquid) show each case type's price for the selected model
  function formatMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: currency }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  function renderCasePrices() {
    var element = picker();
    if (!element) return;
    var model = productSelectedModel();
    loadVariants().then(function (variants) {
      var current = picker();
      if (!current) return;
      current.querySelectorAll('input[type="radio"][data-input-id^="2-"]').forEach(function (input) {
        var label = input.closest('label');
        if (!label) return;
        var variant = variants.filter(function (item) {
          return item.option1 === model && item.option2 === input.value;
        })[0];
        var text = variant ? formatMoney(variant.price) : '';
        if (label.getAttribute('data-case-price') !== text) {
          if (text) label.setAttribute('data-case-price', text);
          else label.removeAttribute('data-case-price');
        }
      });
    });
  }

  function selectOnProduct(model) {
    if (!model) return;
    if (productModels().indexOf(model) === -1) {
      fieldNote = label('labelNotAvailable', model);
      renderField();
      return;
    }
    fieldNote = '';
    chooseOption(1, model);
    renderField();
  }

  /* ---------- panel ---------- */

  function option(text, value, selected, extraClass) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'phone-selector__option' + (extraClass ? ' ' + extraClass : '');
    button.textContent = text;
    if (value !== null) {
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', selected ? 'true' : 'false');
      button.addEventListener('click', function () {
        choose(value);
      });
    }
    return button;
  }

  function groupLabel(text) {
    var p = document.createElement('p');
    p.className = 'phone-selector__group-label';
    p.textContent = text;
    return p;
  }

  function status(text) {
    var p = document.createElement('p');
    p.className = 'phone-selector__status';
    p.textContent = text;
    return p;
  }

  // Models offered in the current context: the product's own models on a product page, otherwise the catalog
  function availableModels() {
    if (context !== 'product') return catalog;
    var own = productModels();
    var known = catalog.filter(function (model) {
      return own.indexOf(model.name) !== -1;
    });
    var unknown = own
      .filter(function (name) {
        return !catalog.some(function (model) {
          return model.name === name;
        });
      })
      .map(function (name, index) {
        return { name: name, brand: name.split(' ')[0], sort: 10000 + index };
      });
    return known.concat(unknown);
  }

  function currentForContext() {
    if (context === 'mine') return mine;
    if (context === 'product') return productSelectedModel();
    return effective();
  }

  function normalize(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/\+/g, ' plus ');
  }

  function matches(name, query) {
    var target = normalize(name);
    var compactTarget = target.replace(/[^a-z0-9]/g, '');
    var compactQuery = query.replace(/[^a-z0-9]/g, '');
    if (compactQuery && compactTarget.indexOf(compactQuery) !== -1) return true;
    return query
      .split(/[^a-z0-9]+/)
      .filter(Boolean)
      .every(function (token) {
        return target.indexOf(token) !== -1;
      });
  }

  function brandsOf(models) {
    var seen = [];
    models.forEach(function (model) {
      if (seen.indexOf(model.brand) === -1) seen.push(model.brand);
    });
    return seen.sort(function (a, b) {
      var ra = BRAND_ORDER.indexOf(a);
      var rb = BRAND_ORDER.indexOf(b);
      if (ra < 0) ra = BRAND_ORDER.length;
      if (rb < 0) rb = BRAND_ORDER.length;
      return ra - rb || a.localeCompare(b);
    });
  }

  function brandButton(brand) {
    var button = option(brand, null, false, 'phone-selector__option--brand');
    var chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    chevron.setAttribute('viewBox', '0 0 24 24');
    chevron.setAttribute('width', '16');
    chevron.setAttribute('height', '16');
    chevron.setAttribute('aria-hidden', 'true');
    chevron.innerHTML =
      '<path fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>';
    button.appendChild(chevron);
    button.addEventListener('click', function () {
      openBrand = brand;
      render();
      var first = list.querySelector('button');
      if (first) first.focus();
    });
    return button;
  }

  function render() {
    list.replaceChildren();
    if (catalogState === 'idle' || catalogState === 'loading') {
      list.appendChild(status(loadingText));
      return;
    }
    if (catalogState === 'error' && context !== 'product') {
      list.appendChild(status(data.labelError));
      return;
    }

    var models = availableModels();
    var current = currentForContext();
    var query = normalize(search.value.trim());

    if (query) {
      back.hidden = true;
      title.textContent = data.labelChoose;
      var found = models.filter(function (model) {
        return matches(model.name, query);
      });
      if (!found.length) list.appendChild(status(data.labelNoResults));
      found.forEach(function (model) {
        list.appendChild(option(model.name, model.name, model.name === current));
      });
      return;
    }

    if (openBrand) {
      back.hidden = false;
      title.textContent = openBrand;
      models
        .filter(function (model) {
          return model.brand === openBrand;
        })
        .forEach(function (model) {
          list.appendChild(option(model.name, model.name, model.name === current));
        });
      return;
    }

    back.hidden = true;
    title.textContent = data.labelChoose;
    if (context !== 'product') list.appendChild(option(data.labelAll, '', !current));

    // Each entry is an exact model name or "Brand:N" = that brand's N newest models (lowest sort_order first,
    // models are already sorted), so new releases show up without editing the setting.
    var popular = [];
    (data.popular || '')
      .split(',')
      .map(function (entry) {
        return entry.trim();
      })
      .filter(Boolean)
      .forEach(function (entry) {
        var rule = entry.match(/^(.+?)\s*:\s*(\d+)$/);
        var picked = rule
          ? models
              .filter(function (model) {
                return model.brand.toLowerCase() === rule[1].toLowerCase();
              })
              .slice(0, Number(rule[2]))
          : models.filter(function (model) {
              return model.name === entry;
            });
        picked.forEach(function (model) {
          if (popular.indexOf(model) === -1) popular.push(model);
        });
      });
    if (popular.length) {
      list.appendChild(groupLabel(data.labelPopular));
      popular.forEach(function (model) {
        list.appendChild(option(model.name, model.name, model.name === current));
      });
      list.appendChild(groupLabel(data.labelChooseModel));
    }

    brandsOf(models).forEach(function (brand) {
      list.appendChild(brandButton(brand));
    });
  }

  function loadCatalog() {
    if (catalogState !== 'idle') return;
    catalogState = 'loading';
    var base = data.catalogUrl || '/';
    var url = base + (base.indexOf('?') === -1 ? '?' : '&') + 'section_id=phone-model-catalog';
    fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Phone model catalog unavailable');
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        catalog = Array.prototype.map
          .call(doc.querySelectorAll('[data-phone-model-catalog] [data-model]'), function (node) {
            return { name: node.dataset.model, brand: node.dataset.brand, sort: Number(node.dataset.sort) || 9999 };
          })
          .sort(function (a, b) {
            return a.sort - b.sort || a.name.localeCompare(b.name);
          });
        catalogState = 'ready';
        if (!root.hidden) {
          var current = currentForContext();
          var match = availableModels().filter(function (model) {
            return model.name === current;
          })[0];
          if (match && !search.value) openBrand = match.brand;
          render();
        }
      })
      .catch(function () {
        catalogState = 'error';
        if (!root.hidden) render();
      });
  }

  function open(trigger, triggerContext) {
    lastTrigger = trigger;
    context = triggerContext || 'mine';
    var current = currentForContext();
    var match = availableModels().filter(function (model) {
      return model.name === current;
    })[0];
    openBrand = match ? match.brand : null;
    search.value = '';
    root.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    document.querySelectorAll('[data-phone-selector-trigger]').forEach(function (button) {
      button.setAttribute('aria-expanded', 'true');
    });
    loadCatalog();
    render();
    // On touch screens, focusing the search field opens the keyboard over the list (and iOS zooms in);
    // focus the close button instead so the brand list stays fully visible
    if (window.matchMedia('(pointer: coarse)').matches) {
      var firstButton = root.querySelector('[data-phone-selector-close]');
      if (firstButton) firstButton.focus({ preventScroll: true });
    } else {
      search.focus();
    }
  }

  function close() {
    if (root.hidden) return;
    root.hidden = true;
    document.documentElement.style.overflow = '';
    document.querySelectorAll('[data-phone-selector-trigger]').forEach(function (button) {
      button.setAttribute('aria-expanded', 'false');
    });
    if (lastTrigger && lastTrigger.isConnected && lastTrigger.offsetParent) lastTrigger.focus();
  }

  /* ---------- applying a choice ---------- */

  function applyToPage() {
    updateTriggers();
    var model = effective();
    if (pageType === 'collection') {
      window.location.href = withModel(window.location.href, model).toString();
    } else if (pageType === 'product') {
      selectOnProduct(model || mine);
      renderBar();
    } else if (model) {
      window.location.href = withModel(data.targetUrl || '/collections/all', model).toString();
    }
  }

  function choose(model) {
    close();
    if (context === 'mine') {
      setMine(model);
      setShopping('');
    } else if (context === 'product') {
      setShopping(model);
    } else {
      setShopping(model || ALL);
    }
    applyToPage();
  }

  /* ---------- wiring ---------- */

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-phone-selector-trigger]');
    if (trigger) {
      event.preventDefault();
      open(trigger, trigger.getAttribute('data-phone-selector-context'));
      return;
    }
    if (event.target.closest('[data-phone-selector-close]')) close();
  });

  back.addEventListener('click', function () {
    openBrand = null;
    render();
  });

  search.addEventListener('input', render);

  document.addEventListener('keydown', function (event) {
    if (root.hidden) return;
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    var focusable = Array.prototype.filter.call(root.querySelectorAll('button, input'), function (element) {
      return !element.hidden && element.getClientRects().length;
    });
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Carry the model into every collection link (menus, tiles, buttons), unless the link sets its own
  function carryModel(event) {
    var model = effective();
    if (!model) return;
    var link = event.target.closest('a[href]');
    if (!link || root.contains(link)) return;
    var url;
    try {
      url = new URL(link.href, window.location.origin);
    } catch (error) {
      return;
    }
    if (url.origin !== window.location.origin) return;
    if (!/\/collections\/[^/]+\/?$/.test(url.pathname)) return;
    if (url.searchParams.has(param)) return;
    url.searchParams.set(param, model);
    link.href = url.toString();
  }
  document.addEventListener('click', carryModel, true);
  document.addEventListener('auxclick', carryModel, true);

  if (pageType === 'product') {
    var startProduct = function () {
      // A model picked in Fabric's own picker (or by this script) counts as "shopping for"
      document.addEventListener('change', function (event) {
        var target = event.target;
        if (!target.closest || !target.closest('variant-picker')) return;
        var value = '';
        if (target.matches('input[type="radio"][data-input-id^="1-"]')) value = target.value;
        if (target.matches('select')) {
          var selected = target.options[target.selectedIndex];
          if (selected && selected.dataset.inputId && selected.dataset.inputId.indexOf('1-') === 0) value = selected.value;
        }
        if (!value) return;
        fieldNote = '';
        selectGalleryMediaForModel(value);
        // Selecting the phone the page opened on (my phone / shopping for) is not a new choice
        var opened = shopping && shopping !== ALL ? shopping : mine;
        if (value !== opened) setShopping(value);
        ensureCaseType(value);
        renderField();
      });

      // Keep the field in sync after Fabric re-renders the picker
      var element = picker();
      if (element && window.MutationObserver) {
        new MutationObserver(function () {
          if (field && !field.isConnected) field = null;
          renderField();
        }).observe(element, { subtree: true, attributes: true, attributeFilter: ['checked'] });
      }

      renderField();
      // Open on the remembered model unless the link already named a variant
      var params = new URLSearchParams(window.location.search);
      // "Show all models" only widens collection lists; a product page still opens on a phone
      var model = shopping && shopping !== ALL ? shopping : mine;
      if (model && !params.has('variant')) {
        selectOnProduct(model);
        if (productModels().indexOf(model) !== -1) ensureCaseType(model);
      }
    };
    if (window.customElements && window.customElements.whenDefined) {
      window.customElements.whenDefined('variant-picker').then(startProduct);
    } else {
      startProduct();
    }
  }

  // Mobile: the selector strip under the header folds away once the page is scrolled (not sticky)
  var barHidden = false;
  var pageWrapper = document.querySelector('.page-wrapper');
  function onScroll() {
    // Only the page's own scroll counts (window, or Fabric's .page-wrapper on desktop), not carousels
    var scroller = document.scrollingElement || document.documentElement;
    var y = Math.max(window.scrollY || 0, scroller.scrollTop || 0, pageWrapper ? pageWrapper.scrollTop : 0);
    var hide = y > 40;
    if (hide === barHidden) return;
    barHidden = hide;
    document.documentElement.classList.toggle('phone-selector-bar-hidden', hide);
  }
  window.addEventListener('scroll', onScroll, { passive: true, capture: true });

  updateTriggers();
  renderBar();
})();
